import { authApi } from '@/lib/api/auth.api'
import { createLogger } from '@/lib/logger'
import { clearAppData, isTauri } from '@/lib/utils/tauri'
import { resetPersistedModuleFilters } from '@/store/filter-scope'
import { toast } from 'sonner'

const logger = createLogger('utils/auth/auth-utils')

async function performLogout(purgeAllLocalData: boolean) {
  let logoutFailed = false

  try {
    // Call the auth API to logout
    await authApi.logout()
  } catch (error) {
    logoutFailed = true
    logger.error('[Auth] Error during logout:', error)
  } finally {
    if (purgeAllLocalData) {
      // This clears local/session storage, IndexedDB, service-worker caches,
      // desktop secure accounts, and Tauri WebView browsing data.
      await clearAppData()
    } else {
      // For desktop, remove only the current account during a normal logout.
      if (isTauri()) {
        try {
          const tauriModule = await import('@/lib/utils/tauri')
          await tauriModule.desktopSecureStorage.deleteTokens()
        } catch (storageError) {
          logger.error('[Auth] Failed to clear desktop storage:', storageError)
        }
      }

      resetPersistedModuleFilters()
      const keysToRemove = [
        'currentOrganizationId',
        'currentUserId',
        'pendingInviteToken',
        'invitedOrganizationName',
        'scrambl-login-session',
        'organization-store',
        'organization-invite-store',
        'user-profile-storage',
      ]

      Object.keys(localStorage).forEach(key => {
        if (
          keysToRemove.includes(key) ||
          key.startsWith('user-profile-image-')
        ) {
          localStorage.removeItem(key)
        }
      })
      sessionStorage.clear()
    }

    if (logoutFailed && !purgeAllLocalData) {
      toast.error(
        'The server could not complete sign out. Local session data was cleared.',
      )
    }

    // A hard replacement resets all in-memory stores and query caches.
    window.location.replace('/login')
  }
}

/** Standard sign-out that preserves unrelated device preferences/accounts. */
export async function handleLogout() {
  await performLogout(false)
}

/**
 * Destructive sign-out used after account deletion. Removes every Scrambl
 * credential, cache, and persisted record on this device before reloading.
 */
export async function handleLogoutAndPurge() {
  await performLogout(true)
}
